<template>
  <div class="row">
    <div class="page-title">
      <div class="row">
        <div class="col-sm-6 col-12">
          <h2>
            Shifts
          </h2>
        </div>
        <div class="col-sm-6 col-12 text-end">
          <a class="btn btn-outline-primary align-items-center me-2" href="/admin/shifts/calendar">
            <i class="fa-solid fa-calendar-days"></i>
            Calendar View
          </a>
          <a class="btn btn-primary align-items-center" href="/admin/shifts/new">
            <i class="fa-regular fa-square-plus"></i>
            Add New
          </a>
        </div>
      </div>
    </div>

    <div class="col-xl-12 xl-100 box-col-6">
      <div class="card">
        <div class="card-body social-list filter-cards-view">
          <div v-if="dataLoaded">
            <DataTable
              :ajax="{ url: `/api/datatable/shifts` }"
              ref="shiftsDataTable" class="table-responsive display" :columns="shiftColumns" />
          </div>
          <div v-else class="text-center py-4">
            <span class="spinner-border spinner-border-sm me-2"></span> Loading...
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import DataTable from 'datatables.net-vue3';
import DataTablesCore from 'datatables.net-bs5';
import { shiftStatus, zonedDateTime, currency } from '../utility/DatatableRenderer';

DataTable.use(DataTablesCore);

const shiftsDataTable = ref(null);
const dataLoaded = ref(false);
const jurisdictionsById = ref({});
const geoZonesById = ref({});

// Names aren't on the wire for Shift (its jurisdiction/geoZone relations are @JsonIgnore'd, unlike
// ServiceJurisdiction.serviceArea) — only serviceJurisdictionId/geoZoneId. Resolve names client-side
// once, before the table renders, rather than racing DataTable's own render against these fetches.
const jurisdictionOrZoneRender = (data, type, row) => {
  if (row.serviceJurisdictionId && jurisdictionsById.value[row.serviceJurisdictionId]) {
    return jurisdictionsById.value[row.serviceJurisdictionId];
  }
  if (row.geoZoneId && geoZonesById.value[row.geoZoneId]) {
    return geoZonesById.value[row.geoZoneId];
  }
  return '<span class="text-muted">All Zones</span>';
};

const shiftColumns = [
  { data: 'startsAt', title: 'Starts', render: zonedDateTime },
  { data: 'endsAt', title: 'Ends', render: zonedDateTime },
  { data: 'guaranteedEarnings', title: 'Guaranteed Earnings', render: currency },
  { data: 'maxDrivers', title: 'Max Drivers' },
  { data: 'serviceJurisdictionId', title: 'Jurisdiction / Zone', render: jurisdictionOrZoneRender },
  { data: 'status', title: 'Status', render: shiftStatus },
  {
    title: 'Action', orderable: false, searchable: false, render: (data, type, row) => {
      return `<a class="btn btn-sm btn-outline-primary" href="/admin/shifts/${row.id}">
                <i class="fas fa-pen-to-square"></i> Edit</a>`;
    }
  }
];

const fetchJurisdictionsAndZones = async () => {
  try {
    const [jurisdictionsResponse, zonesResponse] = await Promise.all([
      fetch('/api/service-jurisdictions/active'),
      fetch('/api/service-area-zones')
    ]);
    if (jurisdictionsResponse.ok) {
      const jurisdictions = await jurisdictionsResponse.json();
      jurisdictionsById.value = Object.fromEntries(jurisdictions.map(j => [j.id, j.name]));
    }
    if (zonesResponse.ok) {
      const zones = await zonesResponse.json();
      geoZonesById.value = Object.fromEntries(zones.map(z => [z.id, z.name]));
    }
  } catch (error) {
    console.error('Error fetching jurisdictions/zones for shift table:', error);
  }
};

onMounted(async () => {
  await fetchJurisdictionsAndZones();
  dataLoaded.value = true;
});
</script>
